<template>
    <div class="overlayConfigPanel" v-if="tool && (clip || tool === 'background')">
        <div class="ocp-flyout">
        <div class="ocp-header">
            <p class="ot-title">{{ toolTitle }}</p>
            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.close-title')" position="left">
                <i class="fa-solid fa-xmark ocp-close" @click="$emit('close')"></i>
            </Tooltip>
        </div>

        <div class="ocp-body">
        <template v-if="tool === 'crop'">
            <p class="ct-hint" v-if="!clip.sourceWidth">{{ $t('dash.post-creator.media-editor.clip-tool.loading-dimensions') }}</p>
            <template v-else>
                <div class="ot-row tc-crop-ratios">
                    <span
                        v-for="ratio in cropRatios"
                        :key="ratio.label"
                        :class="{ active: clip.aspectRatioTarget === (ratio.value === null ? 'custom' : ratio.value) }"
                        @click="$emit('update-crop-ratio', ratio.value)"
                    >{{ ratio.value === null ? $t('dash.post-creator.media-editor.clip-tool.aspect-original') : ratio.label }}</span>
                </div>

                <!-- Ajuster/Remplir only changes how the ratio+position picked below gets
                     rendered (see MediaEditor.vue's renderClipFrame comment) - the picker itself
                     (ratio buttons, drag, width/height px) is identical in both modes. -->
                <p class="ot-title">{{ $t('dash.post-creator.media-editor.clip-tool.fill-mode-title') }}</p>
                <div class="ot-row tc-crop-ratios">
                    <span
                        :class="{ active: (clip.transform.fillMode || 'contain') === 'contain' }"
                        @click="$emit('update-fill-mode', 'contain')"
                    >{{ $t('dash.post-creator.media-editor.clip-tool.fill-mode-contain') }}</span>
                    <span
                        :class="{ active: clip.transform.fillMode === 'cover' }"
                        @click="$emit('update-fill-mode', 'cover')"
                    >{{ $t('dash.post-creator.media-editor.clip-tool.fill-mode-cover') }}</span>
                </div>
                <p class="ct-hint">{{ $t('dash.post-creator.media-editor.clip-tool.fill-mode-hint') }}</p>
                <!-- The preview stays on the full uncropped source the whole time this tool is open
                     (see VideoPreviewCanvas.vue's cropPreviewActive), so the dashed box drawn by
                     CropSelector.vue is where the two modes actually become visible - worth naming
                     rather than leaving as an unexplained line on the video. -->
                <p class="ct-hint">{{ $t('dash.post-creator.media-editor.clip-tool.fill-mode-preview-hint') }}</p>

                <p class="ct-hint">{{ $t('dash.post-creator.media-editor.clip-tool.drag-hint') }}</p>
                <div class="ot-row">
                    <label>{{ $t('dash.post-creator.media-editor.clip-tool.width-px') }}</label>
                    <input type="number" min="10" :max="clip.sourceWidth" :value="cropWidthPx" @change="e => setCropSize(Number(e.target.value), cropHeightPx)">
                </div>
                <div class="ot-row">
                    <label>{{ $t('dash.post-creator.media-editor.clip-tool.height-px') }}</label>
                    <input type="number" min="10" :max="clip.sourceHeight" :value="cropHeightPx" @change="e => setCropSize(cropWidthPx, Number(e.target.value))">
                </div>

                <!-- Formats per network, prioritized to whichever ones are actually selected for
                     this post (see networkRatioSections) - same card style as ImageCropper.vue's
                     own per-network ratio menu (a geometric shape at the target's own aspect
                     ratio, via the same inline aspect-ratio trick), adapted for video-only
                     formats (Reels/Stories/Shorts). -->
                <p class="ot-title">{{ $t('dash.post-creator.media-editor.clip-tool.formats-by-network') }}</p>
                <div class="ot-row" v-if="hasHiddenNetworks">
                    <label class="ot-checkbox-label">
                        <input type="checkbox" v-model="showAllNetworks">
                        {{ $t('dash.post-creator.media-editor.clip-tool.show-all-networks') }}
                    </label>
                </div>
                <div class="tc-network-ratios" v-for="section in networkRatioSections" :key="section.network">
                    <p class="ct-hint tc-network-name">{{ networkDisplayName(section.network) }}</p>
                    <div class="tc-ratio-grid">
                        <div
                            class="tc-ratio-item"
                            v-for="ratio in section.ratios"
                            :key="ratio.titleKey + ratio.label"
                            :class="{ 'tc-ratio-item--active': clip.aspectRatioTarget === ratio.value }"
                            @click="$emit('update-crop-ratio', ratio.value)"
                        >
                            <div class="tc-ratio-shape-wrap">
                                <div class="tc-ratio-shape" :style="{ 'aspect-ratio': ratio.value }">
                                    <p>{{ ratio.label }}</p>
                                </div>
                            </div>
                            <h5>{{ $t('dash.post-creator.media-editor.clip-tool.ratio-' + ratio.titleKey) }}</h5>
                        </div>
                    </div>
                </div>
            </template>
        </template>

        <template v-if="tool === 'rotate'">
            <div class="ot-row tc-crop-ratios">
                <span
                    v-for="deg in [0, 90, 180, 270]"
                    :key="deg"
                    :class="{ active: clip.transform.rotationDeg === deg }"
                    @click="$emit('update-rotation', deg)"
                >{{ deg }}°</span>
            </div>
            <div class="ot-row">
                <label>{{ $t('dash.post-creator.media-editor.clip-tool.rotation-angle') }}</label>
                <input type="range" min="0" max="359" step="1" :value="clip.transform.rotationDeg || 0" @input="e => $emit('update-rotation', Number(e.target.value))">
                <span class="ot-unit">{{ clip.transform.rotationDeg || 0 }}°</span>
            </div>
        </template>

        <template v-if="tool === 'silence'">
            <p class="ct-hint" v-if="clip.sourceType !== 'video'">{{ $t('dash.post-creator.media-editor.clip-tool.no-sound-images') }}</p>
            <template v-else>
                <div class="ct-silence-loading" v-if="detectingSilence">
                    <i class="fa-solid fa-spinner fa-spin"></i>
                    {{ $t('dash.post-creator.media-editor.clip-tool.detecting-silence') }}
                </div>
                <p class="ct-hint" v-else-if="silenceDetectionResult && silenceDetectionResult.clipId === clip.id">
                    <template v-if="silenceDetectionResult.count > 0">{{ $t('dash.post-creator.media-editor.clip-tool.silences-removed', { count: silenceDetectionResult.count }) }}</template>
                    <template v-else>{{ $t('dash.post-creator.media-editor.clip-tool.no-silence-found') }}</template>
                </p>

                <template v-if="silencePreviewActive">
                    <div class="ot-row">
                        <label>
                            {{ $t('dash.post-creator.media-editor.clip-tool.silence-sensitivity') }}
                            <Tooltip :text="$t('dash.post-creator.media-editor.clip-tool.silence-sensitivity-tooltip')" position="bottom" classCustom="ct-info-tooltip">
                                <i class="fa-solid fa-circle-info"></i>
                            </Tooltip>
                        </label>
                        <input
                            type="range" min="0.15" max="0.6" step="0.01"
                            :value="silenceThresholdRatio"
                            @input="e => $emit('update-silence-settings', { thresholdRatio: Number(e.target.value) })"
                        >
                    </div>
                    <div class="ot-row">
                        <label>
                            {{ $t('dash.post-creator.media-editor.clip-tool.silence-min-duration') }}
                            <Tooltip :text="$t('dash.post-creator.media-editor.clip-tool.silence-min-duration-tooltip')" position="bottom" classCustom="ct-info-tooltip">
                                <i class="fa-solid fa-circle-info"></i>
                            </Tooltip>
                        </label>
                        <input
                            type="range" min="200" max="2000" step="50"
                            :value="silenceMinDurationMs"
                            @input="e => $emit('update-silence-settings', { minSilenceDurationMs: Number(e.target.value) })"
                        >
                        <span class="ot-unit">{{ silenceMinDurationMs }}ms</span>
                    </div>
                </template>

                <template v-if="silencePreviewActive">
                    <p class="ct-hint">{{ $t('dash.post-creator.media-editor.clip-tool.segment-on-timeline-hint') }}</p>

                    <div class="ot-row tc-silence-actions">
                        <div class="ot-reposition" @click="$emit('reset-silence')">
                            <i class="fa-solid fa-arrows-left-right"></i> {{ $t('dash.post-creator.media-editor.clip-tool.silence-reset') }}
                        </div>
                        <div class="ot-reposition" @click="$emit('cancel-silence')">
                            <i class="fa-solid fa-xmark"></i> {{ $t('dash.post-creator.media-editor.clip-tool.silence-cancel') }}
                        </div>
                    </div>
                    <div class="ot-reposition tc-silence-validate" @click="$emit('validate-silence')">
                        <i class="fa-solid fa-check"></i> {{ $t('dash.post-creator.media-editor.clip-tool.silence-validate') }}
                    </div>
                </template>
            </template>
        </template>

        <template v-if="tool === 'speed'">
            <p class="ct-hint" v-if="clip.sourceType !== 'video'">{{ $t('dash.post-creator.media-editor.clip-tool.no-speed-images') }}</p>
            <template v-else>
                <p class="ct-hint">{{ $t('dash.post-creator.media-editor.clip-tool.speed-hint') }}</p>
                <div class="ot-row tc-crop-ratios">
                    <span
                        v-for="preset in speedPresets"
                        :key="preset"
                        :class="{ active: currentSpeed === preset }"
                        @click="$emit('update-speed', preset)"
                    >{{ preset }}×</span>
                </div>
                <div class="ot-row" v-if="currentSpeed !== 1">
                    <label class="ot-checkbox-label">
                        <input type="checkbox" :checked="preservePitch" @change="e => $emit('update-preserve-pitch', e.target.checked)">
                        {{ $t('dash.post-creator.media-editor.clip-tool.preserve-pitch') }}
                    </label>
                </div>
            </template>
        </template>

        <template v-if="tool === 'sound'">
            <p class="ct-hint" v-if="clip.sourceType !== 'video'">{{ $t('dash.post-creator.media-editor.clip-tool.no-sound-images') }}</p>
            <template v-else>
                <p class="ct-hint">{{ $t('dash.post-creator.media-editor.clip-tool.sound-hint') }}</p>
                <div class="ot-row">
                    <label class="ot-checkbox-label">
                        <input type="checkbox" :checked="!clip.muted" @change="e => $emit('update-audio', { muted: !e.target.checked })">
                        {{ $t('dash.post-creator.media-editor.clip-tool.original-sound') }}
                    </label>
                </div>
                <div class="ot-row" v-if="!clip.muted">
                    <label>{{ $t('dash.post-creator.media-editor.clip-tool.volume') }}</label>
                    <input type="range" min="0" max="1" step="0.05" :value="clip.volume !== undefined ? clip.volume : 1" @input="e => $emit('update-audio', { volume: Number(e.target.value) })">
                </div>
            </template>
        </template>

        <template v-if="tool === 'background'">
            <p class="ct-hint">{{ $t('dash.post-creator.media-editor.clip-tool.background-hint') }}</p>
            <div class="ot-row tc-crop-ratios">
                <span :class="{ active: bgMode === 'blur' }" @click="onClickBgMode('blur')">{{ $t('dash.post-creator.media-editor.clip-tool.background-blur') }}</span>
                <span :class="{ active: bgMode === 'color' }" @click="onClickBgMode('color')">
                    {{ $t('dash.post-creator.media-editor.clip-tool.background-color-solid') }}
                    <span class="ot-color-dot" :style="{ backgroundColor: bgColor }"></span>
                </span>
            </div>
            <div class="ot-row" v-if="bgMode === 'color'">
                <label>{{ $t('dash.post-creator.media-editor.clip-tool.color') }}</label>
                <input type="color" :value="bgColor" @input="e => $emit('update-background', { color: e.target.value })">
                <span class="ot-color-hex">{{ bgColor.toUpperCase() }}</span>
            </div>
        </template>

        <template v-if="tool === 'transition'">
            <div class="ot-row tc-crop-ratios">
                <span
                    v-for="option in transitionOptions"
                    :key="option.value"
                    :class="{ active: clip.transitionOut.type === option.value }"
                    @click="selectTransitionType(option.value)"
                ><i class="fa-solid" :class="option.icon"></i> {{ $t(option.labelKey) }}</span>
            </div>

            <template v-if="hasDirection">
                <div class="ot-row">
                    <span
                        v-for="dir in directionOptions"
                        :key="dir.value"
                        class="ot-icon-slot"
                        :class="{ active: transitionDirection === dir.value }"
                        :title="$t(dir.labelKey)"
                        @click="selectDirection(dir.value)"
                    ><i class="fa-solid" :class="dir.icon"></i></span>
                </div>
            </template>

            <template v-if="clip.transitionOut.type !== 'none'">
                <div class="ot-row">
                    <label>{{ $t('dash.post-creator.media-editor.clip.duration-label', { sec: (transitionDurationMs / 1000).toFixed(1) }) }}</label>
                    <input type="range" min="100" :max="maxTransitionDurationMs" step="50" :value="transitionDurationMs" @input="e => selectDuration(Number(e.target.value))">
                </div>

                <p class="ot-title">{{ $t('dash.post-creator.media-editor.clip-tool.easing-label') }}</p>
                <div class="ot-row tc-crop-ratios">
                    <span
                        v-for="preset in easingPresets"
                        :key="preset.key"
                        :class="{ active: easingPresetKey === preset.key }"
                        @click="selectEasingPreset(preset)"
                    >{{ $t(preset.labelKey) }}</span>
                    <span :class="{ active: easingPresetKey === 'custom' }">{{ $t('dash.post-creator.media-editor.clip-tool.easing-custom') }}</span>
                </div>
                <EasingCurveEditor :points="easingPoints" @update:points="onEasingPointsChange"/>
            </template>
        </template>
        </div>
        </div>
    </div>
</template>

<script>
import { ASPECT_RATIOS, VIDEO_RATIO_NETWORK_SECTIONS } from '../engine/constants.js'
import { EASING_PRESETS, DEFAULT_EASING_POINTS, matchEasingPreset } from '../engine/easing.js'
import { getEffectiveClipDuration } from '../engine/timelineModel.js'
import EasingCurveEditor from './EasingCurveEditor.vue'
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'

export default {
    name: 'ClipToolPanel',

    components: {
        EasingCurveEditor,
        Tooltip
    },

    props: {
        clip: {
            type: Object,
            default: null
        },
        tool: {
            type: String,
            default: null
        },
        canvasBackground: {
            type: Object,
            default: () => ({ mode: 'blur', color: '#000000' })
        },
        detectingSilence: {
            type: Boolean,
            default: false
        },
        silenceDetectionResult: {
            type: Object,
            default: null
        },
        silenceThresholdRatio: {
            type: Number,
            default: 0.35
        },
        silenceMinDurationMs: {
            type: Number,
            default: 500
        },
        // Whether a review is currently active for *this* clip (see MediaEditor.vue's
        // silencePreview) - shows the sensitivity/duration sliders and the validate/reset/cancel
        // actions once true (the sliders themselves lazily trigger a detection pass on first use
        // if none has run yet this session - see MediaEditor.vue's updateSilenceSettings).
        silencePreviewActive: {
            type: Boolean,
            default: false
        },
        // Which networks (page.network keys - 'instagram', 'tiktok'...) are actually selected for
        // this post - see networkRatioSections, which prioritizes the crop tool's per-network
        // format sections around this instead of always showing every network at once.
        networksSelected: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            cropRatios: ASPECT_RATIOS,
            // User-toggled - see hasHiddenNetworks/networkRatioSections.
            showAllNetworks: false,
            speedPresets: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2],
            transitionOptions: [
                { value: 'none', labelKey: 'dash.post-creator.media-editor.clip.transition-none', icon: 'fa-ban' },
                { value: 'fade', labelKey: 'dash.post-creator.media-editor.clip.transition-fade', icon: 'fa-circle-half-stroke' },
                { value: 'wipe', labelKey: 'dash.post-creator.media-editor.clip.transition-wipe', icon: 'fa-arrows-left-right' },
                { value: 'slide', labelKey: 'dash.post-creator.media-editor.clip.transition-slide', icon: 'fa-arrow-right' },
                { value: 'zoom', labelKey: 'dash.post-creator.media-editor.clip.transition-zoom', icon: 'fa-magnifying-glass' },
                { value: 'blur', labelKey: 'dash.post-creator.media-editor.clip.transition-blur', icon: 'fa-droplet' }
            ],
            directionOptions: [
                { value: 'left', labelKey: 'dash.post-creator.media-editor.clip.direction-left', icon: 'fa-arrow-left' },
                { value: 'right', labelKey: 'dash.post-creator.media-editor.clip.direction-right', icon: 'fa-arrow-right' },
                { value: 'up', labelKey: 'dash.post-creator.media-editor.clip.direction-up', icon: 'fa-arrow-up' },
                { value: 'down', labelKey: 'dash.post-creator.media-editor.clip.direction-down', icon: 'fa-arrow-down' }
            ],
            easingPresets: EASING_PRESETS
        }
    },

    computed: {
        // canvasBackground IS timeline.canvas (width/height/fps/backgroundColor/backgroundMode/...),
        // not a small {mode, color} shape - reading .mode/.color (as this used to, even before this
        // session) always read undefined, silently falling back to 'blur'/'#000000' every time
        // regardless of the real value.
        bgMode() {
            return this.canvasBackground.backgroundMode || 'blur'
        },
        bgColor() {
            return this.canvasBackground.backgroundColor || '#000000'
        },
        currentSpeed() {
            return (this.clip && this.clip.playbackRate) || 1
        },
        preservePitch() {
            return !this.clip || this.clip.preservePitch !== false
        },
        toolTitle() {
            if (this.tool === 'crop') return this.$t('dash.post-creator.media-editor.clip-tool.title-crop')
            if (this.tool === 'rotate') return this.$t('dash.post-creator.media-editor.clip-tool.title-rotate')
            if (this.tool === 'silence') return this.$t('dash.post-creator.media-editor.clip-tool.title-silence')
            if (this.tool === 'speed') return this.$t('dash.post-creator.media-editor.clip-tool.title-speed')
            if (this.tool === 'sound') return this.$t('dash.post-creator.media-editor.clip-tool.title-sound')
            if (this.tool === 'background') return this.$t('dash.post-creator.media-editor.clip-tool.title-background')
            if (this.tool === 'transition') return this.$t('dash.post-creator.media-editor.clip-tool.title-transition')
            return ''
        },

        hasDirection() {
            return this.clip && (this.clip.transitionOut.type === 'wipe' || this.clip.transitionOut.type === 'slide')
        },

        transitionDirection() {
            return (this.clip && this.clip.transitionOut.direction) || 'left'
        },

        transitionDurationMs() {
            return (this.clip && this.clip.transitionOut.durationMs) || 500
        },

        // The transition window is carved out of the *end* of this clip (see getTransitionAt) -
        // a duration longer than the clip's own length would push that window's start before the
        // clip even begins, blending in frames from before its actual start instead of a clean
        // transition. Capped at a generous 5s ceiling otherwise, up from the previous flat 1.5s.
        maxTransitionDurationMs() {
            if (!this.clip) return 5000
            return Math.max(150, Math.min(5000, getEffectiveClipDuration(this.clip) - 50))
        },

        easingPoints() {
            return (this.clip && this.clip.transitionOut.easingPoints) || DEFAULT_EASING_POINTS
        },

        easingPresetKey() {
            return matchEasingPreset(this.easingPoints)
        },

        cropWidthPx() {
            const rect = this.clip.transform.cropRect
            return Math.round(rect.width * this.clip.sourceWidth)
        },

        cropHeightPx() {
            const rect = this.clip.transform.cropRect
            return Math.round(rect.height * this.clip.sourceHeight)
        },

        // Only the networks actually selected for this post, so the crop tool isn't cluttered with
        // formats for platforms this post isn't even going to. Falls back to every network when
        // none is selected yet (or the prop wasn't passed at all) rather than showing nothing -
        // or when the user explicitly asked to see the rest too (see hasHiddenNetworks/showAllNetworks).
        networkRatioSections() {
            if (this.showAllNetworks || !this.networksSelected.length) return VIDEO_RATIO_NETWORK_SECTIONS
            const filtered = VIDEO_RATIO_NETWORK_SECTIONS.filter(section => this.networksSelected.includes(section.network))
            return filtered.length ? filtered : VIDEO_RATIO_NETWORK_SECTIONS
        },

        // The "show all" checkbox only makes sense (and only appears) when the list is actually
        // being filtered down to a subset right now - no point offering to reveal networks that
        // are already all showing.
        hasHiddenNetworks() {
            return !this.showAllNetworks && this.networksSelected.length > 0 &&
                VIDEO_RATIO_NETWORK_SECTIONS.some(section => !this.networksSelected.includes(section.network))
        }
    },

    methods: {
        // Was referenced in the template but never defined - every click silently threw
        // (this.onClickBgMode is not a function) instead of ever reaching $emit, which is why
        // nothing downstream (MediaEditor.vue's onUpdateBackground, the watcher, $forceUpdate)
        // ever ran no matter what else got fixed.
        onClickBgMode(mode) {
            this.$emit('update-background', { mode })
        },

        networkDisplayName(network) {
            if (network === 'twitter') return 'X'
            return network.charAt(0).toUpperCase() + network.slice(1)
        },

        // Centered crop of the given pixel size (clamped to the source's own dimensions).
        setCropSize(widthPx, heightPx) {
            const sourceWidth = this.clip.sourceWidth
            const sourceHeight = this.clip.sourceHeight
            const width = Math.min(Math.max(10, widthPx), sourceWidth) / sourceWidth
            const height = Math.min(Math.max(10, heightPx), sourceHeight) / sourceHeight
            const x = (1 - width) / 2
            const y = (1 - height) / 2
            this.$emit('update-crop-rect', { x, y, width, height })
        },

        // Every handler below always spreads the clip's *current* transitionOut before overriding
        // just the one field it owns - switching e.g. the transition type must not silently drop
        // an already-picked easing curve or direction, the way each control's own dedicated event
        // (rather than a single generic "patch" one) could otherwise make easy to miss.
        selectTransitionType(type) {
            this.emitTransition({ ...this.clip.transitionOut, type, durationMs: type === 'none' ? 0 : this.transitionDurationMs })
        },

        selectDirection(direction) {
            this.emitTransition({ ...this.clip.transitionOut, direction })
        },

        selectDuration(durationMs) {
            this.emitTransition({ ...this.clip.transitionOut, durationMs })
        },

        selectEasingPreset(preset) {
            this.emitTransition({ ...this.clip.transitionOut, easing: preset.key, easingPoints: preset.points.slice() })
        },

        onEasingPointsChange(points) {
            this.emitTransition({ ...this.clip.transitionOut, easing: 'custom', easingPoints: points })
        },

        emitTransition(transitionOut) {
            this.$emit('update-transition', transitionOut)
        }
    }
}
</script>

<style lang="scss" scoped></style>
